import { ValidationItems } from '@kanda-libs/ks-component-ts';
import { useFinanceCalculatorContext } from 'components/shared/FinanceCalculator/context';
import {
  calculateMaxFromRate,
  calculateMinFromRate,
} from 'components/shared/FinanceCalculator/helpers';
import { useMemo } from 'react';
import { formatToCurrency, groupByAprBNPL } from 'utils';

export interface MonthlyPaymentHook {
  isLoading: boolean;
  validation: ValidationItems;
}

export default function useMonthlyPayment(): MonthlyPaymentHook {
  const { isLoading, stored, rates } = useFinanceCalculatorContext();

  const apr = useMemo(() => stored.apr, [stored]);
  const type = useMemo(() => stored.type, [stored]);

  const grouped = useMemo(() => {
    if (isLoading || !rates) return undefined;
    return groupByAprBNPL(rates);
  }, [isLoading, rates]);

  const max = useMemo(() => {
    if (!apr || !grouped) return 0;
    const selected = grouped[apr];
    if (!selected) return 0;
    return Math.max(...selected.map((rate) => calculateMaxFromRate(rate)));
  }, [apr, grouped]);

  const min = useMemo(() => {
    if (!apr || !grouped) return 0;
    const selected = grouped[apr];
    if (!selected) return 0;
    return Math.min(...selected.map((rate) => calculateMinFromRate(rate)));
  }, [apr, grouped]);

  const validation = useMemo(
    () => ({
      validate: {
        value: {
          typeRequired: (value?: string | number | boolean) =>
            typeof value === 'number' && value > 0 && type === 'borrowAmount',
          minCustom: (value?: string | number | boolean) => {
            if (typeof value !== 'number') return false;
            if (!min) return true;
            return value >= min;
          },
          maxCustom: (value?: string | number | boolean) => {
            if (typeof value !== 'number') return false;
            if (!max) return true;
            return value <= max;
          },
        },
        message: {
          typeRequired: 'You must provide a monthly payment amount',
          minCustom: `Monthly payment must be above ${formatToCurrency(min)}`,
          maxCustom: `Monthly payment cannot exceed ${formatToCurrency(max)}`,
        },
      },
    }),
    [type, min, max],
  );

  return {
    isLoading,
    validation,
  };
}
